<template>
  <div class="marketing-page popup-manager">
    <MarketingToolHeader
      eyebrow="Web & App"
      title="Popups"
      description="Steuere aufmerksamkeitsstarke Hinweise für Webseite und Roody mit klarer Zielgruppe, Laufzeit und Aktion."
      icon="mdi-message-badge-outline">
      <template v-slot:actions>
        <v-btn icon class="icon-action" :loading="loading" aria-label="Popups aktualisieren" title="Popups aktualisieren" @click="fetchPopups">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn color="#009640" depressed class="primary-action" @click="createPopup"> <v-icon left>mdi-plus</v-icon>Neues Popup </v-btn>
      </template>
    </MarketingToolHeader>

    <section class="metric-grid" aria-label="Popup Übersicht">
      <v-card dark flat class="metric-card">
        <span class="metric-icon icon-green"><v-icon>mdi-broadcast</v-icon></span>
        <div>
          <span>Aktiv</span><strong>{{ loading ? "–" : sortedPopups.active.length }}</strong
          ><small>werden angezeigt</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon icon-amber"><v-icon>mdi-clock-outline</v-icon></span>
        <div>
          <span>Geplant</span><strong>{{ loading ? "–" : sortedPopups.upcoming.length }}</strong
          ><small>starten später</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon"><v-icon>mdi-archive-outline</v-icon></span>
        <div>
          <span>Archiv</span><strong>{{ loading ? "–" : sortedPopups.past.length }}</strong
          ><small>nicht mehr sichtbar</small>
        </div>
      </v-card>
      <v-card dark flat class="metric-card">
        <span class="metric-icon"><v-icon>mdi-monitor-cellphone</v-icon></span>
        <div>
          <span>Gesamt</span><strong>{{ loading ? "–" : popups.length }}</strong
          ><small>Webseite &amp; Roody</small>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="content-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Ausspielung</div>
          <h2>Aktiv &amp; geplant</h2>
          <p>Überblick über Zielgruppe, Kanal, Studios und Laufzeit.</p>
        </div>
        <span class="count-pill">{{ sortedPopups.active.length + sortedPopups.upcoming.length }} Popups</span>
      </div>

      <div v-if="sortedPopups.active.length || sortedPopups.upcoming.length" class="popup-grid">
        <v-card
          v-for="item in [...sortedPopups.active, ...sortedPopups.upcoming]"
          :key="item._id"
          dark
          flat
          class="popup-card"
          role="button"
          tabindex="0"
          @click="editPopup(item._id)"
          @keydown.enter="editPopup(item._id)">
          <div class="popup-card-top">
            <span class="channel-icon"
              ><v-icon>{{
                item.popupLocation === "website" ? "mdi-web" : item.popupLocation === "roody" ? "mdi-cellphone" : "mdi-devices"
              }}</v-icon></span
            >
            <span class="status-pill" :class="popupStatusColor(item)">
              <v-icon small>{{ item.status === "aktiv" ? "mdi-broadcast" : "mdi-clock-start" }}</v-icon
              >{{ item.statusText }}
            </span>
          </div>
          <h3>{{ item.title }}</h3>
          <div class="fact-list">
            <span><v-icon small>mdi-map-marker-radius-outline</v-icon>{{ popupLocationText(item.popupLocation) }}</span>
            <span><v-icon small>mdi-account-group-outline</v-icon>{{ item.employeesOnly ? "Nur Mitarbeiter" : "Alle Benutzer" }}</span>
            <span><v-icon small>mdi-store-outline</v-icon>{{ (item.studios || []).length }} Studio(s)</span>
          </div>
          <div class="card-footer">
            <span><v-icon small>mdi-calendar-range-outline</v-icon>{{ popupTime(item) }}</span>
            <v-icon small>mdi-arrow-right</v-icon>
          </div>
        </v-card>
      </div>

      <div v-else-if="!loading" class="surface-empty">
        <span><v-icon>mdi-message-plus-outline</v-icon></span>
        <h3>Noch keine laufenden Popups</h3>
        <p>Erstelle den ersten Hinweis für Webseite oder Roody.</p>
        <v-btn outlined color="#58d388" class="secondary-action" @click="createPopup"><v-icon left>mdi-plus</v-icon>Popup erstellen</v-btn>
      </div>
    </v-card>

    <v-card v-if="sortedPopups.past.length" dark flat class="content-surface archive-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Historie</div>
          <h2>Archiv</h2>
          <p>Abgelaufene Popups können weiterhin geöffnet und angepasst werden.</p>
        </div>
        <span class="count-pill">{{ sortedPopups.past.length }} archiviert</span>
      </div>
      <div class="archive-grid">
        <v-card
          v-for="item in sortedPopups.past"
          :key="item._id"
          dark
          flat
          class="archive-card"
          role="button"
          tabindex="0"
          @click="editPopup(item._id)"
          @keydown.enter="editPopup(item._id)">
          <span class="archive-icon"><v-icon>mdi-archive-outline</v-icon></span>
          <div>
            <span>Archiviert</span>
            <h3>{{ item.title }}</h3>
            <p>{{ popupTime(item) }}</p>
          </div>
          <v-icon small>mdi-arrow-right</v-icon>
        </v-card>
      </div>
    </v-card>

    <v-dialog
      v-model="showPopupDialog"
      max-width="980px"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="marketing-editor-dialog">
      <v-card dark class="editor-card">
        <div class="editor-header">
          <span class="editor-mark"><v-icon>mdi-message-badge-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Popup Editor</div>
            <h2>{{ popup._id ? "Popup bearbeiten" : "Neues Popup erstellen" }}</h2>
            <p>Inhalt, Ausspielung und Aktionslink zentral konfigurieren.</p>
          </div>
          <v-btn icon class="icon-action editor-close" aria-label="Dialog schliessen" @click="cancel"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="editor-body">
          <v-row>
            <v-col cols="12" md="7">
              <section class="editor-section">
                <div class="section-heading">
                  <v-icon>mdi-text-box-outline</v-icon>
                  <div>
                    <h3>Inhalt</h3>
                    <p>Titel, Text und Zielstudios</p>
                  </div>
                </div>
                <v-text-field
                  v-model="popup.title"
                  label="Titel"
                  placeholder="z.B. 11-Tage-Kampagne"
                  outlined
                  dark
                  class="modern-field"
                  hide-details="auto" />
                <marketing-wysiwyg v-model="popup.text" :editor-toolbar="customToolbar" />
                <v-select
                  v-model="popup.studios"
                  :items="studios"
                  item-text="name"
                  item-value="_id"
                  label="Studios"
                  multiple
                  chips
                  outlined
                  dark
                  attach
                  class="modern-field studio-select"
                  hide-details="auto" />
              </section>
            </v-col>

            <v-col cols="12" md="5">
              <section class="editor-section">
                <div class="section-heading">
                  <v-icon>mdi-image-outline</v-icon>
                  <div>
                    <h3>Bild &amp; Laufzeit</h3>
                    <p>Motiv und zeitliche Steuerung</p>
                  </div>
                </div>
                <slim-cropper :key="popup.image" class="image-cropper" :options="slimOptionsExercise">
                  <img v-if="popup.image" :src="linkPreview + '/img/' + popup.image" alt="Popup Bild" />
                  <input type="file" accept="image/*" />
                </slim-cropper>
                <span class="format-hint">Empfohlenes Format: 16:9 · 1920 × 1080 px</span>
                <v-menu v-model="showAfterMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedShowAfter"
                      label="Anzeigen ab"
                      prepend-inner-icon="mdi-calendar-start"
                      readonly
                      clearable
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="popup.showAfter = null" />
                  </template>
                  <v-date-picker v-model="popup.showAfter" color="#009640" locale="de" dark no-title @input="showAfterMenu = false" />
                </v-menu>
                <v-menu v-model="hideAfterMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedHideAfter"
                      label="Ausblenden ab"
                      prepend-inner-icon="mdi-calendar-end"
                      readonly
                      clearable
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto"
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="popup.hideAfter = null" />
                  </template>
                  <v-date-picker v-model="popup.hideAfter" color="#009640" locale="de" dark no-title @input="hideAfterMenu = false" />
                </v-menu>
              </section>
            </v-col>

            <v-col cols="12">
              <section class="editor-section compact-section">
                <div class="section-heading">
                  <v-icon>mdi-tune-variant</v-icon>
                  <div>
                    <h3>Ausspielung</h3>
                    <p>Kanal, Zielgruppe und Startseite</p>
                  </div>
                </div>
                <div class="option-grid">
                  <div class="option-block">
                    <span class="option-label">Kanal</span>
                    <v-radio-group v-model="popup.popupLocation" row hide-details class="option-control">
                      <v-radio label="Webseite" value="website" color="#009640" />
                      <v-radio label="Roody" value="roody" color="#009640" />
                      <v-radio label="Beide" value="both" color="#009640" />
                    </v-radio-group>
                  </div>
                  <div v-if="['website', 'both'].includes(popup.popupLocation)" class="option-block">
                    <span class="option-label">Webseite</span>
                    <v-switch
                      v-model="popup.visibility.everywhere"
                      label="Auf Startseite anzeigen"
                      color="#009640"
                      inset
                      hide-details
                      class="option-control" />
                  </div>
                  <div v-if="['roody', 'both'].includes(popup.popupLocation)" class="option-block">
                    <span class="option-label">Zielgruppe</span>
                    <v-switch v-model="popup.employeesOnly" label="Nur für Mitarbeiter" color="#009640" inset hide-details class="option-control" />
                  </div>
                </div>
              </section>
            </v-col>

            <v-col cols="12">
              <section class="editor-section compact-section">
                <div class="section-heading">
                  <v-icon>mdi-link-variant</v-icon>
                  <div>
                    <h3>Button &amp; Aktionslink</h3>
                    <p>Optionaler Call-to-Action im Popup</p>
                  </div>
                </div>
                <v-row dense>
                  <v-col cols="12" md="4"
                    ><v-text-field
                      v-model="popup.linkText"
                      label="Button Text"
                      placeholder="z.B. Jetzt buchen"
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto"
                  /></v-col>
                  <v-col cols="12" md="3" class="switch-column"
                    ><v-switch v-model="isExternalLink" label="Externer Link" color="#009640" inset hide-details class="option-control"
                  /></v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-if="!isExternalLink"
                      v-model="popup.link"
                      label="Interner Pfad"
                      prefix="app.wellcomefit.ch"
                      placeholder="/referral"
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto" />
                    <v-text-field
                      v-else
                      v-model="popup.link"
                      label="Externe URL"
                      placeholder="https://..."
                      outlined
                      dark
                      class="modern-field"
                      hide-details="auto" />
                  </v-col>
                </v-row>
              </section>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="editor-actions">
          <span class="validation-hint">Titel, Inhalt oder Bild sowie mindestens ein Studio sind erforderlich.</span>
          <v-spacer />
          <v-btn text class="cancel-action" @click="cancel">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :disabled="!formValidated" :loading="saving" @click="save">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import api from "@/api"
import moment from "moment"
import MarketingToolHeader from "./MarketingToolHeader.vue"
import MarketingWysiwyg from "./MarketingWysiwyg.vue"

export default {
  name: "popups",
  components: { MarketingToolHeader, "marketing-wysiwyg": MarketingWysiwyg, "slim-cropper": Slim },
  data() {
    return {
      loading: false,
      saving: false,
      isExternalLink: false,
      studios: [],
      popup: {},
      showPopupDialog: false,
      popups: [],
      customToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link", "clean"],
      ],
      slimOptionsExercise: {
        ratio: "16:9",
        initialImage: "/img/einmalig/imageUploadPlaceholder.jpg",
        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        label: "Bild hochladen",
        labelLoading: "Bild wird hochgeladen",
        statusUploadSuccess: "gespeichert",
        service: this.uploadPicture,
        serviceFormat: "file",
        instantEdit: true,
        push: true,
      },
      showAfterMenu: false,
      hideAfterMenu: false,
    }
  },
  async created() {
    this.fetchPopups()
    const studios = await api.fetchStudios()
    this.studios = studios.filter((item) => item.code !== "ot")
    this.resetPopup()
  },
  computed: {
    linkPreview() {
      return ""
    },
    sortedPopups() {
      const categories = { active: [], upcoming: [], past: [] }
      if (!this.popups) return categories
      this.popups.forEach((popup) => {
        const { status } = this.getPopupStatus(popup)
        if (status === "aktiv") categories.active.push(popup)
        else if (status === "geplant") categories.upcoming.push(popup)
        else categories.past.push(popup)
      })
      return categories
    },
    formattedShowAfter() {
      return this.popup.showAfter ? moment(this.popup.showAfter).format("DD.MM.YYYY") : ""
    },
    formattedHideAfter() {
      return this.popup.hideAfter ? moment(this.popup.hideAfter).format("DD.MM.YYYY") : ""
    },
    formValidated() {
      if (!this.popup.title) return false
      return this.popup.title.length > 3 && (this.popup.studios || []).length > 0 && (this.popup.image || (this.popup.text || "").length > 3)
    },
  },
  methods: {
    getPopupStatus(popup) {
      const now = moment()
      if (popup.hideAfter && moment(popup.hideAfter).isBefore(now, "day")) return { status: "inaktiv", text: "Inaktiv" }
      if (popup.showAfter && moment(popup.showAfter).isAfter(now, "day")) {
        const diff = moment(popup.showAfter).diff(now, "days") + 1
        return { status: "geplant", text: `Startet in ${diff} Tag${diff > 1 ? "en" : ""}` }
      }
      return { status: "aktiv", text: "Aktiv" }
    },
    popupStudioNames(popup) {
      return (popup.studios || []).map((entry) => {
        if (entry && typeof entry === "object" && entry.name) return entry.name
        const studioId = entry && typeof entry === "object" ? entry._id : entry
        const studio = this.studios.find((item) => item._id === studioId)
        return studio ? studio.name : "Unbekannt"
      })
    },
    popupTime(popup) {
      const { showAfter, hideAfter } = popup
      const format = (date) => moment(date).format("DD.MM.YYYY")
      if (showAfter && hideAfter) return `Von ${format(showAfter)} bis ${format(hideAfter)}`
      if (showAfter) return `Ab ${format(showAfter)}`
      if (hideAfter) return `Bis ${format(hideAfter)}`
      return "Dauerhaft aktiv"
    },
    popupLocationText(location) {
      if (location === "roody") return "Nur Roody"
      if (location === "website") return "Nur Webseite"
      return "Webseite und Roody"
    },
    popupStatusColor(popup) {
      const { status } = this.getPopupStatus(popup)
      if (status === "aktiv") return "is-visible"
      if (status === "geplant") return "is-upcoming"
      return "is-past"
    },
    createPopup() {
      this.resetPopup()
      this.showPopupDialog = true
    },
    async editPopup(id) {
      this.resetPopup()
      try {
        const popup = await api.PopupService.get(id)
        if (!popup || !popup._id) throw Error("Fehler beim Laden")
        if (popup.showAfter) popup.showAfter = moment(popup.showAfter).format("YYYY-MM-DD")
        if (popup.hideAfter) popup.hideAfter = moment(popup.hideAfter).format("YYYY-MM-DD")
        if (!popup.visibility) popup.visibility = { everywhere: false, paths: [] }
        popup.studios = (popup.studios || []).map((studio) => studio._id || studio)
        this.isExternalLink = Boolean(popup.link && popup.link.startsWith("http"))
        this.popup = popup
        this.showPopupDialog = true
      } catch (e) {
        this.$toast.error("Fehler beim Laden des Popups")
      }
    },
    cancel() {
      this.showPopupDialog = false
      this.resetPopup()
    },
    async save() {
      const payload = JSON.parse(JSON.stringify(this.popup))
      payload.studios = (payload.studios || []).map((item) => (typeof item === "object" ? item._id : item))
      if (payload.link && !this.isExternalLink && !payload.link.startsWith("/")) payload.link = "/" + payload.link
      if (!payload.link) payload.linkText = ""

      this.saving = true
      try {
        if (payload._id) await api.PopupService.update(payload._id, payload)
        else await api.PopupService.create(payload)
        this.$toast.success("Popup erfolgreich gespeichert")
        this.cancel()
        await this.fetchPopups()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.saving = false
      }
    },
    resetPopup() {
      this.isExternalLink = false
      this.popup = {
        popupLocation: "roody",
        hideAfter: null,
        showAfter: null,
        visibility: { everywhere: false, paths: [] },
        title: "",
        text: "",
        image: "",
        employeesOnly: false,
        studios: [],
        link: "",
        linkText: "",
      }
    },
    async fetchPopups() {
      this.loading = true
      try {
        const popups = await api.PopupService.getAll()
        this.popups = popups.map((item) => ({ ...item, status: this.getPopupStatus(item).status, statusText: this.getPopupStatus(item).text }))
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Popups")
      } finally {
        this.loading = false
      }
    },
    uploadPicture(formdata, progress, success, failure) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      api
        .uploadImage({ art: "popup", formData })
        .then((data) => {
          if (data.status) {
            this.popup.image = data.name
            success(data)
          }
        })
        .catch((error) => failure(error))
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon,
.channel-icon,
.archive-icon,
.editor-mark,
.surface-empty > span {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.metric-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.content-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.archive-surface {
  margin-top: 1rem;
}

.surface-header,
.popup-card-top,
.card-footer,
.editor-header,
.section-heading {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow,
.option-label {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.editor-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.editor-header p,
.section-heading p,
.surface-empty p,
.archive-card p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.count-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  white-space: nowrap;
}

.popup-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.popup-card,
.archive-card {
  border: 1px solid var(--border);
  outline: none;
  background: var(--surface) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.popup-card {
  min-height: 16.25rem;
  padding: 1rem;
  border-radius: 1.125rem !important;
}

.popup-card:focus-visible,
.archive-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.popup-card-top,
.card-footer {
  justify-content: space-between;
  gap: 0.75rem;
}

.channel-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.channel-icon .v-icon,
.archive-icon .v-icon,
.editor-mark .v-icon,
.surface-empty > span .v-icon {
  color: currentColor;
}

.status-pill {
  min-height: 1.75rem;
  gap: 0.3125rem;
  padding: 0.25rem 0.625rem;
  color: #ffffff;
}

.status-pill .v-icon {
  color: currentColor;
}

.is-visible {
  background: rgba(0, 150, 64, 0.88);
}

.is-upcoming {
  background: rgba(217, 119, 6, 0.9);
}

.is-past {
  background: rgba(82, 82, 91, 0.9);
}

.popup-card h3,
.archive-card h3,
.surface-empty h3,
.section-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.popup-card h3 {
  min-height: 2.7rem;
  margin-top: 1.125rem;
}

.fact-list {
  display: grid;
  gap: 0.625rem;
  margin: 1rem 0;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
}

.fact-list span,
.card-footer span {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  color: #c7c7cb;
  font-size: 0.875rem;
}

.fact-list .v-icon,
.card-footer .v-icon {
  color: #8f9095;
}

.card-footer {
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.card-footer span {
  overflow: hidden;
  color: var(--muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.surface-empty {
  display: flex;
  min-height: 17rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2.5rem;
  text-align: center;
}

.surface-empty > span {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty .secondary-action {
  margin-top: 1rem;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.archive-card {
  display: grid;
  min-width: 0;
  grid-template-columns: 2.75rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border-radius: 1rem !important;
  opacity: 0.82;
}

.archive-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.055);
}

.archive-card > div {
  min-width: 0;
}

.archive-card > div > span {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.archive-card h3,
.archive-card p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editor-card {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  font-size: 1rem;
}

.editor-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.editor-header > div {
  min-width: 0;
}

.editor-close {
  margin-left: auto;
}

.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  max-height: none;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-section {
  height: 100%;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.0625rem;
  background: rgba(255, 255, 255, 0.028);
}

.compact-section {
  height: auto;
}

.section-heading {
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 1rem;
}

.section-heading > .v-icon {
  color: #58d388;
}

.modern-field {
  margin-bottom: 0.875rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.option-control ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.studio-select {
  margin: 0;
}

.image-cropper {
  width: 100%;
  overflow: hidden;
  border-radius: 0.8125rem;
}

.format-hint,
.validation-hint {
  color: var(--muted);
  font-size: 0.75rem;
}

.format-hint {
  display: block;
  margin: 0.625rem 0 1rem;
  text-align: center;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.option-block {
  min-height: 5.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
}

.option-control {
  margin-top: 0.5rem;
}

.switch-column {
  display: flex;
  align-items: center;
}

.editor-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.cancel-action {
  min-height: 2.75rem;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .popup-card:hover,
  .archive-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .popup-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid,
  .option-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 6.5rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.125rem;
    height: 2.125rem;
  }

  .metric-card small {
    display: none;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .popup-grid,
  .archive-grid,
  .option-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .editor-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .editor-mark,
  .editor-header p,
  .validation-hint {
    display: none;
  }

  .editor-body {
    max-height: none;
    padding: 0.75rem !important;
  }

  .editor-section {
    padding: 0.875rem;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .editor-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
